import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Event } from '../types/event.types';
import { locationCache } from '../services/LocationCacheService';
import { smartListenerService, normalizeSupabaseEvent, getStableDate } from '../supabase/SmartListenerService';
import geohash from 'ngeohash';

// 🌍 GEOHASH: Precision 5 = ~4.9km x 4.9km (couverture ~15km avec 9 zones)
const GEOHASH_PRECISION = 5;

// ✅ OPTIMISATION ARRAY : Array vide stable pour éviter les re-renders
const EMPTY_EVENTS_ARRAY: Event[] = [];

// ✅ FIX: Fonction stable pour générer la signature d'un event (inclut updatedAt)
const getEventSignature = (event: Event): string => {
  const updatedAtStr = event.updatedAt instanceof Date
    ? event.updatedAt.getTime().toString()
    : (event.updatedAt || '');
  return `${event.id}:${updatedAtStr}`;
};

/**
 * 🌍 HOOK GÉOGRAPHIQUE - Version optimisée avec SmartListenerService
 *
 * ARCHITECTURE : IndexedDB est la source de vérité unique
 * - SmartListener sauvegarde dans IndexedDB
 * - Le callback trigger un reload depuis IndexedDB
 * - Le hook ne reçoit pas les events directement du callback
 */
export const useSmartGeoEvents = (
  location: { latitude: number; longitude: number } | undefined,
  radius: number = 10
) => {
  const [rawEvents, setRawEvents] = useState<Event[]>(EMPTY_EVENTS_ARRAY);
  const [loading, setLoading] = useState(!!location);
  const [error, setError] = useState<Error | null>(null);
  const lastUpdateRef = useRef<Date | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [cacheSource, setCacheSource] = useState<'indexedDB' | 'none'>('none');

  // ✅ OPTIMISATION ARRAY : Mémoriser la signature pour stabilité complète
  // FIX: Utiliser rawEvents directement comme dépendance (pas JSON.stringify qui s'évalue à chaque render)
  const eventsSignature = useMemo(() => {
    return rawEvents.map(getEventSignature).sort().join(',');
  }, [rawEvents]);

  // ✅ OPTIMISATION ARRAY : Stabiliser les événements avec signature mémorisée
  const events = useMemo(() => {
    if (rawEvents.length === 0) {
      return EMPTY_EVENTS_ARRAY;
    }
    return [...rawEvents];
  }, [eventsSignature]);

  // ✅ OPTIMISATION 1 : ID consumer stable avec useRef
  const consumerIdRef = useRef<string>();
  if (!consumerIdRef.current) {
    consumerIdRef.current = `geo-${Math.random().toString(36).substr(2, 9)}`;
  }

  // ✅ FIX RACE CONDITION : Tracker si le SmartListener a confirmé ses données
  // En navigation privée, IndexedDB est vide au premier appel → loading passait à false
  // trop tôt, affichant "no events" avant que les vraies données arrivent de Supabase.
  const hasListenerConfirmed = useRef(false);

  // ✅ OPTIMISATION 2 : Location stabilisée
  const stableLocation = useMemo(() => {
    return location;
  }, [location?.latitude, location?.longitude]);

  // ✅ ARCHITECTURE ORIGINALE : handleEventsUpdate avec useRef pour éviter recréation
  // Le callback NE REÇOIT PAS les events - il trigger un reload depuis IndexedDB
  const handleEventsUpdateRef = useRef<(() => void) | null>(null);

  handleEventsUpdateRef.current = () => {
    if (!stableLocation) {
      return;
    }

    const loadGeoEvents = async () => {
      // 🔧 FIX: Ne pas remettre loading=true si le listener a déjà confirmé
      // Sinon on flash les skeletons à chaque notification SmartListener
      if (!hasListenerConfirmed.current) {
        setLoading(true);
      }
      setError(null);
      let rawIndexedEvents: any[] | null = null;

      try {
        // 🌍 GEOHASH: Calculer le geohash central et ses 8 voisins
        const centerHash = geohash.encode(
          stableLocation.latitude,
          stableLocation.longitude,
          GEOHASH_PRECISION
        );
        const neighborHashes = geohash.neighbors(centerHash);
        // Centre + 8 voisins = 9 zones couvertes (fusion automatique)
        const allHashes = [centerHash, ...neighborHashes];

        // ✅ ARCHITECTURE : Lire depuis IndexedDB avec fusion des zones geohash
        rawIndexedEvents = await locationCache.getGeoEventsByGeohashes(allHashes);

        if (rawIndexedEvents && rawIndexedEvents.length > 0) {
          // ✅ Comparer signatures pour éviter re-renders inutiles
          const currentSignature = rawEvents.map(getEventSignature).sort().join(',');
          const newSignature = rawIndexedEvents.map(getEventSignature).sort().join(',');

          if (currentSignature === newSignature) {
            setCacheSource('indexedDB');
            setLoading(false);
            return; // Pas de changement
          }

          console.log('🔄 [useSmartGeoEvents] Changement détecté - mise à jour des events');

          // Normaliser si nécessaire (optimisé car déjà frozen si du cache)
          const normalizedEvents = rawIndexedEvents.map(event => {
            if (Object.isFrozen(event)) {
              return event;
            }
            return normalizeSupabaseEvent(event);
          });

          setRawEvents(normalizedEvents);
          setCacheSource('indexedDB');
        } else {
          setRawEvents(EMPTY_EVENTS_ARRAY);
          setCacheSource('none');
        }

        // Mettre à jour lastUpdate
        const now = Date.now();
        const stableDate = getStableDate(Math.floor(now / 1000) * 1000);
        lastUpdateRef.current = stableDate;
        setLastUpdate(stableDate);

      } catch (err) {
        console.error('❌ [useSmartGeoEvents] Erreur chargement:', err);
        setError(err as Error);
        setLoading(false);
      } finally {
        // FIX RACE CONDITION : Ne pas couper le loading si IndexedDB est vide
        // et que le SmartListener n'a pas encore livré ses données initiales.
        // Sinon on flash "no events" avant que les vrais events arrivent de Supabase.
        const hasEvents = rawIndexedEvents && rawIndexedEvents.length > 0;
        if (hasEvents || hasListenerConfirmed.current) {
          setLoading(false);
        }
        // Si 0 events et pas de confirmation listener → loading reste true
      }
    };

    loadGeoEvents();
  };

  // Wrapper stable qui ne change jamais
  // ✅ ARCHITECTURE : Le callback IGNORE les events passés - il trigger juste un reload
  // FIX RACE CONDITION : Quand SmartListener appelle ce callback, on sait que les données
  // ont été livrées (même si vides = zone réellement vide)
  const stableHandleEventsUpdate = useCallback(() => {
    hasListenerConfirmed.current = true;
    handleEventsUpdateRef.current?.();
  }, []);

  useEffect(() => {
    if (!stableLocation) {
      setRawEvents(EMPTY_EVENTS_ARRAY);
      return;
    }

    // 🚀 S'abonner au SmartListener
    // Le callback sera appelé quand les données changent dans IndexedDB
    const unsubscribe = smartListenerService.subscribeToGeoEvents(
      stableLocation.latitude,
      stableLocation.longitude,
      radius,
      consumerIdRef.current!,
      stableHandleEventsUpdate // Trigger reload, pas réception directe
    );

    // Chargement initial depuis IndexedDB (sans marquer hasListenerConfirmed)
    // Si le cache est vide, loading restera true jusqu'au callback SmartListener
    hasListenerConfirmed.current = false;
    handleEventsUpdateRef.current?.();

    // Safety timeout : si le SmartListener n'a pas encore notifié après 8s,
    // relire IndexedDB une dernière fois (les events y sont peut-être déjà)
    // puis forcer loading=false pour ne pas bloquer l'UI indéfiniment
    const safetyTimeout = setTimeout(() => {
      if (!hasListenerConfirmed.current) {
        console.warn('⏱️ [useSmartGeoEvents] Safety timeout - relecture IndexedDB');
        // Marquer comme confirmé + relire IndexedDB (via stableHandleEventsUpdate)
        // Les events sont probablement déjà dans IndexedDB mais la notification
        // du SmartListener n'est pas encore arrivée
        stableHandleEventsUpdate();
      }
    }, 8000);

    // Cleanup
    return () => {
      unsubscribe();
      clearTimeout(safetyTimeout);
    };
  }, [stableLocation, radius, stableHandleEventsUpdate]);

  // ✅ PERF FIX: Fonction refresh stable (au lieu d'un inline arrow recréé chaque render)
  const refresh = useCallback(() => {
    stableHandleEventsUpdate();
  }, [stableHandleEventsUpdate]);

  // ✅ PERF FIX: Retour mémorisé pour éviter de créer un nouvel objet à chaque render
  return useMemo(() => ({
    events,
    loading,
    error,
    lastUpdate,
    cacheSource,
    refresh
  }), [events, loading, error, lastUpdate, cacheSource, refresh]);
};
